import {get, post} from '@/api/clients';
import {AuthResponse} from '@/store/authStore';

export interface LoginResponse {
  ok: boolean;
  user: {
    id: string;
    name: string;
    isAdmin: boolean;
  };
  accessToken: string;
  tokenType: 'LOGIN';
  expiresIn: number;
}

export interface RefreshResponse {
  ok: boolean;
  accessToken: string;
  tokenType: 'LOGIN';
  expiresIn: number;
}

export interface LogoutResponse {
  ok: boolean;
  message: string;
}

/**
 * (POST) 로그인
 * @param id 아이디
 * @param password 비밀번호
 */
export const login = async (id: string, password: string): Promise<LoginResponse> => {
  return await post<LoginResponse>('/users/login', {id, password});
};

/**
 * (POST) 토큰 갱신 - LOGIN 타입만 지원
 * @returns 새로운 accessToken
 */
export const refreshToken = async (): Promise<RefreshResponse> => {
  return await post<RefreshResponse>('/users/refresh');
};

/**
 * (POST) 로그아웃 - 모든 쿠키 삭제
 */
export const logout = async (): Promise<LogoutResponse> => {
  return await post<LogoutResponse>('/users/logout');
};

/**
 * (POST) 비밀번호 재설정
 * @param userId 사용자 ID
 * @param password 새 비밀번호
 */
export const resetPassword = async (userId: string, password: string): Promise<{message: string}> => {
  return await post<{message: string}>('/users/reset-password', {userId, password});
};

/**
 * (GET) 현재 사용자 정보 조회 - Authorization Header 우선
 * @returns 사용자 정보 (LOGIN 또는 CI 타입)
 */
export const getMe = async (): Promise<AuthResponse | null> => {
  return await get<AuthResponse | null>('/users/me');
};

/**
 * (GET) 사용자 인증 상태 확인 - Authorization Header 우선
 * @returns 인증 여부
 */
export const checkAuth = async (): Promise<boolean> => {
  return await get<boolean>('/users/check');
};

/**
 * (GET) 토큰 상세 정보 조회 (디버깅용)
 * @returns 토큰 정보
 */
export const getTokenInfo = async (): Promise<
  | {
      subject: string;
      authType: string;
      issuedAt: Date;
      expiration: Date;
      isExpired: boolean;
    }
  | {error: string; message: string}
> => {
  return await get('/users/token-info');
};

/**
 * 토큰이 곧 만료되는지 확인 (클라이언트 사이드)
 * @param expiresAt 만료 시간 (timestamp)
 * @param thresholdMinutes 임계값 (분, 기본 5분)
 * @returns 곧 만료 여부
 */
export const isTokenExpiringSoon = (expiresAt: number | null, thresholdMinutes: number = 5): boolean => {
  if (!expiresAt) return true;

  const now = Date.now();
  const threshold = thresholdMinutes * 60 * 1000;

  return expiresAt - now < threshold;
};

/**
 * 토큰 타입 확인
 * @param authType 인증 타입
 * @returns 토큰 타입별 정보
 */
export const getTokenTypeInfo = (authType: 'LOGIN' | 'CI' | undefined) => {
  switch (authType) {
    case 'LOGIN':
      return {
        canRefresh: true,
        displayName: '로그인',
        maxDuration: '30분',
        refreshable: true
      };
    case 'CI':
      return {
        canRefresh: false,
        displayName: '본인인증',
        maxDuration: '1시간',
        refreshable: false
      };
    default:
      return {
        canRefresh: false,
        displayName: '인증 없음',
        maxDuration: '0분',
        refreshable: false
      };
  }
};
